<template>
  <AppChat />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { useInitialCookies } from './composables/general/useInitialCookies';
import { ASYNC_KEY_PLANS } from '~/models/AsyncDataKeys.model';

const { $api } = useNuxtApp();
const { functionalConsented } = useCookieConsent();
const scrollLockStore = useMyScrollLockStore();
const { pageShouldBeScrollLocked } = storeToRefs(scrollLockStore);
useScrollLockForCookieConsent();
const config = useRuntimeConfig();
const planStore = usePlanStore();
const analyticsEventsStore = useAnalyticsEventsStore();
useInitialCookies();

const { eventsToPushToDatalayer } = storeToRefs(analyticsEventsStore);

useScript(
  {
    src: 'https://optimizely.teliacompany.com/cdn/js/28346260213.js',
    type: 'text/javascript',
    tagPosition: 'head',
    async: true,
    referrerpolicy: false,
    crossorigin: false,
  },
  {
    trigger: functionalConsented,
    tagPriority: 'high',
  },
);

if (config.public.onetrustCookieConsentDomainScript !== 'skipOneTrustCookie') {
  useScript({
    src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
    type: 'text/javascript',
    async: true,
    defer: true,
    'data-language': 'sv',
    'data-domain-script': config.public.onetrustCookieConsentDomainScript || '',
  });
}

const analyticsScript = useScript(
  {
    src: `https://collect.halebop.se/gtm.js?id=GTM-PTXPXGR&l=dataLayer`,
    type: 'text/javascript',
    async: true,
    tagPosition: 'bodyClose',
    crossorigin: false,
  },
  {
    trigger: 'manual',
  },
);

useScript({
  src: 'https://x964.halebop.se/script.js',
  type: 'text/javascript',
  async: true,
  defer: true,
  tagPosition: 'bodyClose',
  crossorigin: false,
});

const giosgSnippet = `(function(w, t, f) {
  var s='script',o='_giosg',h='https://service.giosg.com',e,n;
  e=t.createElement(s);e.async=1;e.src=h+'/live2/'+f;
  w[o]=w[o]||function(){(w[o]._e=w[o]._e||[]).push(arguments)};
  w[o]._c=f;w[o]._h=h;n=t.getElementsByTagName(s)[0];
  n.parentNode.insertBefore(e,n);
})(window,document,"146accd6-5672-11f0-850b-0242ac120005");`;

const giosgDataUri =
  'data:text/javascript;charset=utf-8,' + encodeURIComponent(giosgSnippet);

useScript(
  {
    src: giosgDataUri,
    type: 'text/javascript',
    async: true,
    tagPosition: 'bodyClose',
  },
  {
    trigger: functionalConsented,
  },
);

onMounted(() => {
  if (import.meta.client) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    analyticsScript.load();
  }
});

await useAsyncData(ASYNC_KEY_PLANS, async () => {
  const plansResponse = await $api.planModule.getAllPlans();
  planStore.processAndSetPlans(plansResponse);
  return plansResponse;
});

watch(pageShouldBeScrollLocked, (value: boolean) => {
  document.body.style.overflow = value ? 'hidden' : '';
});

watch(
  eventsToPushToDatalayer,
  (newEvents, previousEvents) => {
    if (!import.meta.client) return;
    if (newEvents.length === previousEvents?.length) {
      return;
    }

    const events = newEvents.slice();
    let hasChangedState = false;

    for (const event of events) {
      if (event.isPushedToDatalayer === false) {
        hasChangedState = true;
        const { event: eventName, ...eventObjectData } = toRaw(
          event.eventObject,
        );
        const data = {
          event: eventName,
          ...eventObjectData,
        };

        window.dataLayer = window.dataLayer || [];
        if (Object.prototype.hasOwnProperty.call(data, 'ecommerce')) {
          window.dataLayer.push({
            ecommerce: null,
          });
        }

        window.dataLayer.push(data);
        event.isPushedToDatalayer = true;
      }
    }

    if (!hasChangedState) {
      return;
    }

    analyticsEventsStore.eventsToPushToDatalayer = events;
  },
  { immediate: true },
);
</script>
